'use client';
import { type ImageField, type LinkField } from 'lib/types';
import { cn } from 'lib/utils';
import { HeaderSection, Wrapper } from 'ui';
import TestimonialCardsFull from './TestimonialCardsFull';
import TestimonialCardsMobile from './TestimonialCardsMobile';
import TestimonialCardSingle from './TestimonialCardSingle';

export type TestimonialCardsProps = {
  className?: string;
  eyebrow?: string;
  title?: string;
  cards: {
    tag?: string;
    image?: ImageField;
    logo?: ImageField;
    content: string;
    author?: string;
    authorOrg?: string;
    authorTitle?: string;
    hideLeftQuote?: boolean;
    hideRightQuote?: boolean;
    link?: LinkField;
  }[];
};

const TestimonialCards = ({
  className,
  eyebrow,
  title,
  cards,
}: TestimonialCardsProps) => {
  // Filter cards to ensure we have between 2-3 cards
  const filteredCards = cards.slice(0, 3);

  // Schema markup for testimonials
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: title || 'Customer Testimonials',
    itemListElement: filteredCards.map((card, index) => ({
      '@type': 'Review',
      position: index + 1,
      reviewBody: card.content,
      ...(card.author && {
        author: {
          '@type': 'Person',
          name: card.author,
          ...(card.authorTitle && { jobTitle: card.authorTitle }),
          ...(card.authorOrg && {
            worksFor: {
              '@type': 'Organization',
              name: card.authorOrg,
            },
          }),
        },
      }),
    })),
  };

  return (
    <>
      {/* Schema markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <Wrapper
        as="section"
        aria-label={title || 'Testimonials'}
        className={cn('flex flex-col gap-10 lg:gap-12', className)}
      >
        <HeaderSection
          alignment="left"
          eyebrowAsTitle={!title}
          {...{ eyebrow, title }}
        />

        {filteredCards.length === 1 ? (
          <TestimonialCardSingle cards={filteredCards} />
        ) : (
          <div>
            <TestimonialCardsMobile cards={filteredCards} />
            <TestimonialCardsFull cards={filteredCards} />
          </div>
        )}
      </Wrapper>
    </>
  );
};

export default TestimonialCards;
